import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Navbar } from "@/components/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Mail, Phone, Package, Lock, ChevronDown, Pause, Play, XCircle, AlertTriangle, CalendarDays, CreditCard, CheckCircle2, ArrowRightLeft, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { profileUpdateSchema, signUpSchema } from "@/lib/validation";
import type { Profile } from "@/types/database";

const Settings = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Check if this is a first-time setup flow
  const isSetupMode = searchParams.get("setup") === "true";
  const setupPlan = searchParams.get("plan");
  const setupPriceId = searchParams.get("priceId");
  
  const [saving, setSaving] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [pausingSubscription, setPausingSubscription] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteStep, setDeleteStep] = useState<"initial" | "survey" | "confirm">("initial");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  
  const [profile, setProfile] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    subscription_type: "",
    street: "",
    city: "",
    zip: "",
    state: "",
    county: "",
    gate_code: "",
  });

  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [pickupsUsed, setPickupsUsed] = useState<number>(0);
  const [loadingPickups, setLoadingPickups] = useState(false);

  const SUBSCRIPTION_PLANS: Record<string, { name: string; price: string; priceId: string; pickupsPerCycle: number }> = {
    monthly: { name: "Monthly Pickup", price: "$14.99/month", priceId: "price_1SuSODPhLqfgxDXsLKU7rEBs", pickupsPerCycle: 1 },
    "bi-weekly": { name: "Bi-Weekly Pickup", price: "$24.99/month", priceId: "price_1SuSOXPhLqfgxDXsDwd31EXd", pickupsPerCycle: 2 },
  };

  // Demo mode: allow access without auth
  // useEffect(() => {
  //   if (!loading && !user) {
  //     navigate("/auth");
  //   }
  // }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProfile();
      checkSubscription();
      fetchPickupsUsed();
    }
  }, [user]);

  const checkSubscription = async () => {
    setCheckingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setSubscriptionInfo(data);
    } catch (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error("Error checking subscription:", error);
      }
    } finally {
      setCheckingSubscription(false);
    }
  };

  const fetchPickupsUsed = async () => {
    if (!user) return;
    setLoadingPickups(true);
    try {
      // Get pickups completed in the current billing cycle (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data, error } = await supabase
        .from("user_pickups")
        .select("id")
        .eq("user_id", user.id)
        .eq("status", "completed")
        .gte("completed_at", thirtyDaysAgo.toISOString());
      
      if (error) throw error;
      setPickupsUsed(data?.length || 0);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching pickups used:", error);
      }
    } finally {
      setLoadingPickups(false);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to open subscription portal",
        variant: "destructive",
      });
    } finally {
      setManagingSubscription(false);
    }
  };

  const handlePauseSubscription = async () => {
    setPausingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke("pause-subscription");
      if (error) throw error;
      toast({
        title: "Subscription Paused",
        description: "Your subscription has been paused. You won't be billed until you resume.",
      });
      checkSubscription(); // Refresh subscription status
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to pause subscription",
        variant: "destructive",
      });
    } finally {
      setPausingSubscription(false);
    }
  };

  const handleResumeSubscription = async () => {
    setPausingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke("resume-subscription");
      if (error) throw error;
      toast({
        title: "Subscription Resumed",
        description: "Your subscription is now active again.",
      });
      checkSubscription(); // Refresh subscription status
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to resume subscription",
        variant: "destructive",
      });
    } finally {
      setPausingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription");
      if (error) throw error;
      toast({
        title: "Subscription Canceled",
        description: `Your subscription will end on ${new Date(data.cancelAt).toLocaleDateString()}. You can still use the service until then.`,
      });
      setShowCancelConfirm(false);
      checkSubscription(); // Refresh subscription status
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription",
        variant: "destructive",
      });
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId }
      });
      if (error) throw error;
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout",
        variant: "destructive",
      });
    }
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("*")
        .eq("id", user!.id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        const profileData = data as Profile;
        setProfile({
          email: profileData.email || "",
          first_name: (profileData as any).first_name || "",
          last_name: (profileData as any).last_name || "",
          phone_number: profileData.phone_number || "",
          subscription_type: profileData.subscription_type || "",
          street: profileData.street || "",
          city: profileData.city || "",
          zip: profileData.zip || "",
          state: profileData.state || "",
          county: profileData.county || "",
          gate_code: profileData.gate_code || "",
        });
      }
    } catch (error) {
      // Log only in development to prevent info leakage
      if (import.meta.env.DEV) {
        console.error("Error fetching profile:", error);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required name fields (always)
    if (!profile.first_name?.trim() || !profile.last_name?.trim()) {
      toast({
        title: "Validation Error",
        description: "First name and last name are required",
        variant: "destructive",
      });
      return;
    }

    // For setup mode, validate required fields
    if (isSetupMode) {
      const missingFields: string[] = [];
      if (!profile.street?.trim()) missingFields.push("Street Address");
      if (!profile.city?.trim()) missingFields.push("City");
      if (!profile.zip?.trim()) missingFields.push("ZIP Code");
      if (!profile.state?.trim()) missingFields.push("State");
      if (!profile.county?.trim()) missingFields.push("County");
      if (!profile.phone_number?.trim()) missingFields.push("Phone Number");

      if (missingFields.length > 0) {
        toast({
          title: "Required Fields Missing",
          description: `Please complete: ${missingFields.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
    }

    // Validate profile data
    const validation = profileUpdateSchema.safeParse({
      phone_number: profile.phone_number,
      street: profile.street,
      city: profile.city,
      zip: profile.zip,
      state: profile.state,
      county: profile.county,
      gate_code: profile.gate_code,
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast({
        title: "Validation Error",
        description: firstError.message,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      const { error } = await (supabase as any)
        .from("profiles")
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone_number: profile.phone_number,
          street: profile.street,
          city: profile.city,
          zip: profile.zip,
          state: profile.state,
          county: profile.county,
          gate_code: profile.gate_code,
        })
        .eq("id", user!.id);

      if (error) throw error;

      // If in setup mode with a plan, redirect to Stripe checkout
      if (isSetupMode && setupPriceId) {
        toast({
          title: "Profile saved!",
          description: "Redirecting to payment...",
        });

        const { data, error: checkoutError } = await supabase.functions.invoke("create-checkout", {
          body: { priceId: setupPriceId }
        });

        if (checkoutError) throw checkoutError;

        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } else if (isSetupMode) {
        // Setup mode without plan - still redirect to dashboard
        toast({
          title: "Account created!",
          description: "Your account is pending admin approval.",
        });
        navigate("/dashboard");
        return;
      }

      toast({
        title: "Settings saved",
        description: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error saving settings",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <Navbar />
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-4xl font-bold mb-2">
              {isSetupMode ? "Complete Your Profile" : "Account Settings"}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {isSetupMode 
                ? "Please fill in your details to complete account setup"
                : "Manage your profile and payments"
              }
            </p>
            {isSetupMode && (
              <p className="text-xs text-primary mt-2">
                Fields marked with * are required
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Collapsible defaultOpen={isSetupMode}>
              <Card className="shadow-medium">
                <CollapsibleTrigger className="w-full text-left">
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <User className="w-5 h-5 text-primary" />
                          Personal Information
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Your account details and contact information
                        </CardDescription>
                      </div>
                      <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          First Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="first_name"
                          type="text"
                          placeholder="John"
                          value={profile.first_name}
                          onChange={(e) =>
                            setProfile({ ...profile, first_name: e.target.value })
                          }
                          maxLength={50}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name <span className="text-destructive">*</span></Label>
                        <Input
                          id="last_name"
                          type="text"
                          placeholder="Doe"
                          value={profile.last_name}
                          onChange={(e) =>
                            setProfile({ ...profile, last_name: e.target.value })
                          }
                          maxLength={50}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile.email}
                        disabled
                        className="bg-muted"
                      />
                      <p className="text-xs text-muted-foreground">
                        Email cannot be changed
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Phone Number {isSetupMode && <span className="text-primary">*</span>}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(123) 456-7890"
                        value={profile.phone_number}
                        onChange={(e) =>
                          setProfile({ ...profile, phone_number: e.target.value })
                        }
                        required={isSetupMode}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="street">Street Address {isSetupMode && <span className="text-primary">*</span>}</Label>
                        <Input
                          id="street"
                          placeholder="123 Main St"
                          value={profile.street}
                          onChange={(e) =>
                            setProfile({ ...profile, street: e.target.value })
                          }
                          required={isSetupMode}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">City {isSetupMode && <span className="text-primary">*</span>}</Label>
                        <Input
                          id="city"
                          placeholder="City"
                          value={profile.city}
                          onChange={(e) =>
                            setProfile({ ...profile, city: e.target.value })
                          }
                          required={isSetupMode}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State {isSetupMode && <span className="text-primary">*</span>}</Label>
                        <Select
                          value={profile.state}
                          onValueChange={(value) =>
                            setProfile({ ...profile, state: value })
                          }
                          required={isSetupMode}
                        >
                          <SelectTrigger id="state">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-border">
                            <SelectItem value="CA">California</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Currently only available in California
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zip">ZIP Code {isSetupMode && <span className="text-primary">*</span>}</Label>
                        <Input
                          id="zip"
                          type="text"
                          placeholder="12345"
                          value={profile.zip}
                          onChange={(e) =>
                            setProfile({ ...profile, zip: e.target.value })
                          }
                          pattern="\d{5}(-\d{4})?"
                          maxLength={10}
                          required={isSetupMode}
                        />
                        <p className="text-xs text-muted-foreground">
                          Format: 12345 or 12345-6789
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="county">County {isSetupMode && <span className="text-primary">*</span>}</Label>
                        <Select
                          value={profile.county}
                          onValueChange={(value) =>
                            setProfile({ ...profile, county: value })
                          }
                          required={isSetupMode}
                        >
                          <SelectTrigger id="county">
                            <SelectValue placeholder="Select county" />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-border">
                            <SelectItem value="Orange County">Orange County</SelectItem>
                            <SelectItem value="Los Angeles County">Los Angeles County</SelectItem>
                            <SelectItem value="San Bernardino County">San Bernardino County</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Coming soon to counties not disclosed
                        </p>
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="gate_code">Gate Code / Access Instructions (Optional)</Label>
                        <Input
                          id="gate_code"
                          placeholder="e.g., #1234 or left gate, call for entry"
                          value={profile.gate_code}
                          onChange={(e) =>
                            setProfile({ ...profile, gate_code: e.target.value })
                          }
                          maxLength={200}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      This address will be used for package pickups
                    </p>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <Collapsible defaultOpen={false}>
              <Card className="shadow-medium">
                <CollapsibleTrigger className="w-full text-left">
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Package className="w-5 h-5 text-primary" />
                          Subscription Plan
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Manage your pickup service subscription
                        </CardDescription>
                      </div>
                      <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6">
                    {checkingSubscription ? (
                      <p className="text-sm text-muted-foreground">Checking subscription...</p>
                    ) : subscriptionInfo?.subscribed ? (
                      <div className="space-y-6">
                        {/* Status banners */}
                        {subscriptionInfo.paused && (
                          <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <Pause className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-amber-700">Subscription Paused</span>
                          </div>
                        )}
                        {subscriptionInfo.cancelAtPeriodEnd && (
                          <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-destructive" />
                            <span className="text-sm font-medium text-destructive">
                              Cancels on {new Date(subscriptionInfo.subscriptionEnd).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        
                        {/* Current Plan - Highlighted in Green */}
                        <div className="p-5 border-2 border-green-500/50 rounded-xl bg-green-500/5 relative">
                          <Badge className="absolute -top-2.5 left-4 bg-green-600 text-white hover:bg-green-600">
                            Your Current Plan
                          </Badge>
                          <div className="flex items-center justify-between mt-2">
                            <div>
                              <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                                {subscriptionInfo.subscriptionType === "monthly" ? "Monthly Pickup" : 
                                 subscriptionInfo.subscriptionType === "bi-weekly" ? "Bi-Weekly Pickup" : "Active Subscription"}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {subscriptionInfo.subscriptionType === "monthly" ? "$14.99/month • 1 pickup per month" : 
                                 subscriptionInfo.subscriptionType === "bi-weekly" ? "$24.99/month • 2 pickups per month" : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {subscriptionInfo.paused ? (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
                                  <Pause className="w-3 h-3 mr-1" /> Paused
                                </Badge>
                              ) : subscriptionInfo.cancelAtPeriodEnd ? (
                                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                                  <XCircle className="w-3 h-3 mr-1" /> Canceling
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Next Renewal Date Banner */}
                        {(subscriptionInfo.subscriptionEnd || subscriptionInfo.subscription_end) && !subscriptionInfo.cancelAtPeriodEnd && !subscriptionInfo.paused && (
                          <div className="p-4 border border-primary/20 rounded-lg bg-primary/5">
                            <div className="flex items-center gap-3">
                              <CreditCard className="w-5 h-5 text-primary flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium">Next Renewal Date</p>
                                <p className="text-lg font-semibold text-primary">
                                  {new Date(subscriptionInfo.subscriptionEnd || subscriptionInfo.subscription_end).toLocaleDateString('en-US', { 
                                    weekday: 'long',
                                    month: 'long', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  You will be charged {SUBSCRIPTION_PLANS[subscriptionInfo.subscriptionType as keyof typeof SUBSCRIPTION_PLANS]?.price || 'your plan rate'} on this date
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Subscription Stats Grid */}
                        <div className="grid grid-cols-2 gap-3">
                          {/* Billing Status / Access Until */}
                          <div className="p-4 border rounded-lg bg-muted/30">
                            <div className="flex items-center gap-2 mb-1">
                              <CalendarDays className="w-4 h-4 text-muted-foreground" />
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                {subscriptionInfo.cancelAtPeriodEnd ? "Access Until" : 
                                 subscriptionInfo.paused ? "Paused Since" : "Billing Cycle Ends"}
                              </p>
                            </div>
                            <p className="text-lg font-semibold">
                              {(subscriptionInfo.subscriptionEnd || subscriptionInfo.subscription_end)
                                ? new Date(subscriptionInfo.subscriptionEnd || subscriptionInfo.subscription_end).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : checkingSubscription ? "Loading..." : "—"
                              }
                            </p>
                          </div>
                          
                          {/* Pickups Used */}
                          <div className="p-4 border rounded-lg bg-muted/30">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pickups This Cycle</p>
                            </div>
                            {loadingPickups ? (
                              <p className="text-lg font-semibold text-muted-foreground">Loading...</p>
                            ) : (
                              <div className="flex items-baseline gap-1">
                                <p className="text-lg font-semibold">{pickupsUsed}</p>
                                <p className="text-sm text-muted-foreground">
                                  / {SUBSCRIPTION_PLANS[subscriptionInfo.subscriptionType as keyof typeof SUBSCRIPTION_PLANS]?.pickupsPerCycle || 1}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Subscription Management Actions */}
                        <div className="space-y-3">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Manage Subscription</p>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {/* Change Plan Button */}
                            <Button
                              onClick={handleManageSubscription}
                              disabled={managingSubscription}
                              variant="outline"
                              className="w-full"
                            >
                              <ArrowRightLeft className="w-4 h-4 mr-2" />
                              {managingSubscription ? "Opening..." : "Change Plan"}
                            </Button>
                            
                            {/* Pause/Resume Button */}
                            {!subscriptionInfo.cancelAtPeriodEnd && (
                              subscriptionInfo.paused ? (
                                <Button
                                  onClick={handleResumeSubscription}
                                  disabled={pausingSubscription}
                                  variant="outline"
                                  className="w-full"
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  {pausingSubscription ? "Resuming..." : "Resume"}
                                </Button>
                              ) : showPauseConfirm ? (
                                <div className="sm:col-span-2 p-3 border border-amber-500/30 rounded-lg bg-amber-500/5 space-y-2">
                                  <p className="text-sm text-amber-700 font-medium">
                                    Pause your subscription?
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Billing will stop. You can resume anytime.
                                  </p>
                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => setShowPauseConfirm(false)}
                                      variant="outline"
                                      size="sm"
                                      className="flex-1"
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={() => {
                                        setShowPauseConfirm(false);
                                        handlePauseSubscription();
                                      }}
                                      disabled={pausingSubscription}
                                      size="sm"
                                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                                    >
                                      {pausingSubscription ? "Pausing..." : "Yes, Pause"}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => setShowPauseConfirm(true)}
                                  disabled={pausingSubscription}
                                  variant="outline"
                                  className="w-full"
                                >
                                  <Pause className="w-4 h-4 mr-2" />
                                  Pause
                                </Button>
                              )
                            )}
                            
                            {/* Cancel Button */}
                            {!subscriptionInfo.cancelAtPeriodEnd && !showPauseConfirm && (
                              showCancelConfirm ? (
                                <div className="sm:col-span-3 p-3 border border-destructive/30 rounded-lg bg-destructive/5 space-y-2">
                                  <p className="text-sm text-destructive font-medium">
                                    Cancel your subscription?
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Access continues until {subscriptionInfo.subscriptionEnd 
                                      ? new Date(subscriptionInfo.subscriptionEnd).toLocaleDateString('en-US', {
                                          month: 'long',
                                          day: 'numeric', 
                                          year: 'numeric'
                                        })
                                      : 'period end'}. You won't be billed again.
                                  </p>
                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => setShowCancelConfirm(false)}
                                      variant="outline"
                                      size="sm"
                                      className="flex-1"
                                    >
                                      Keep Subscription
                                    </Button>
                                    <Button
                                      onClick={handleCancelSubscription}
                                      disabled={cancelingSubscription}
                                      variant="destructive"
                                      size="sm"
                                      className="flex-1"
                                    >
                                      {cancelingSubscription ? "Canceling..." : "Yes, Cancel"}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  onClick={() => setShowCancelConfirm(true)}
                                  variant="ghost"
                                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Expired Subscription Banner */}
                        {subscriptionInfo?.expired && (
                          <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5">
                            <div className="flex items-center gap-3">
                              <XCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                              <div>
                                <p className="font-medium text-destructive">Subscription Expired</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  Your subscription ended on {new Date(subscriptionInfo.expiredAt).toLocaleDateString('en-US', {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}. Resubscribe to continue scheduling pickups.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <p className="text-sm text-muted-foreground">
                          {subscriptionInfo?.expired ? "Choose a plan to resubscribe:" : "Choose a subscription plan to get started"}
                        </p>
                        <div className="grid gap-3">
                          {Object.entries(SUBSCRIPTION_PLANS).map(([key, plan]) => {
                            const isSelected = profile.subscription_type === key;
                            return (
                              <div
                                key={key}
                                className={`p-4 border rounded-lg transition-colors ${
                                  isSelected ? "border-primary/40 bg-transparent" : "hover:border-primary"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">{plan.name}</p>
                                    <p className="text-sm text-muted-foreground">{plan.price}</p>
                                  </div>
                                  <Button
                                    onClick={() => {
                                      if (!isSelected) {
                                        setProfile({ ...profile, subscription_type: key });
                                        handleSubscribe(plan.priceId);
                                      }
                                    }}
                                    size="sm"
                                    variant={subscriptionInfo?.expired ? "default" : "outline"}
                                    disabled={isSelected}
                                    className={isSelected ? "bg-transparent border-border cursor-not-allowed opacity-60" : ""}
                                  >
                                    {isSelected ? "Selected" : subscriptionInfo?.expired ? "Resubscribe" : "Subscribe"}
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <Collapsible defaultOpen={false}>
              <Card className="shadow-medium">
                <CollapsibleTrigger className="w-full text-left">
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Lock className="w-5 h-5 text-primary" />
                          Change Password
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Update your account password
                        </CardDescription>
                      </div>
                      <ChevronDown className="w-5 h-5 text-muted-foreground transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        placeholder="Enter current password"
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, currentPassword: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Enter new password"
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, newPassword: e.target.value })
                        }
                        minLength={6}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                        }
                        minLength={6}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      onClick={async () => {
                        if (passwordData.newPassword !== passwordData.confirmPassword) {
                          toast({
                            title: "Passwords don't match",
                            description: "Please make sure both passwords are the same.",
                            variant: "destructive",
                          });
                          return;
                        }
                        const passwordValidation = signUpSchema.shape.password.safeParse(passwordData.newPassword);
                        if (!passwordValidation.success) {
                          toast({
                            title: "Invalid password",
                            description: passwordValidation.error.errors[0].message,
                            variant: "destructive",
                          });
                          return;
                        }
                        setChangingPassword(true);
                        try {
                          // Verify current password by re-authenticating
                          const { error: signInError } = await supabase.auth.signInWithPassword({
                            email: profile.email,
                            password: passwordData.currentPassword,
                          });
                          if (signInError) {
                            toast({
                              title: "Incorrect current password",
                              description: "Please enter your correct current password.",
                              variant: "destructive",
                            });
                            setChangingPassword(false);
                            return;
                          }
                          
                          const { error } = await supabase.auth.updateUser({
                            password: passwordData.newPassword,
                          });
                          if (error) throw error;
                          toast({
                            title: "Password updated",
                            description: "Your password has been changed successfully.",
                          });
                          setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                        } catch (error: any) {
                          toast({
                            title: "Error changing password",
                            description: error.message || "Please try again.",
                            variant: "destructive",
                          });
                        } finally {
                          setChangingPassword(false);
                        }
                      }}
                    >
                      {changingPassword ? "Updating..." : "Update Password"}
                    </Button>

                    {/* Delete Account Section */}
                    <div className="pt-6 mt-6 border-t border-destructive/20">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-destructive">
                          <Trash2 className="w-4 h-4" />
                          <h4 className="font-medium">Delete Account</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Permanently delete your account and all associated data. This action cannot be undone.
                        </p>
                        
                        {/* Step 1: Initial Confirmation Dialog */}
                        <AlertDialog open={showDeleteDialog && deleteStep === "initial"} onOpenChange={(open) => {
                          if (!open) {
                            setShowDeleteDialog(false);
                            setDeleteStep("initial");
                          }
                        }}>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              className="w-full"
                              onClick={() => {
                                setShowDeleteDialog(true);
                                setDeleteStep("initial");
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete My Account
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                                <AlertTriangle className="w-5 h-5" />
                                Confirm you want to delete your account
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete your account, profile data, and all package history.
                                <strong> This action cannot be undone.</strong>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => {
                                setShowDeleteDialog(false);
                                setDeleteStep("initial");
                              }}>
                                No, Keep My Account
                              </AlertDialogCancel>
                              <Button
                                variant="destructive"
                                onClick={() => setDeleteStep("survey")}
                              >
                                Yes, Delete My Account
                              </Button>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        {/* Step 2: Optional Survey + Final Deletion */}
                        <AlertDialog open={showDeleteDialog && deleteStep === "survey"} onOpenChange={(open) => {
                          if (!open) {
                            setShowDeleteDialog(false);
                            setDeleteStep("initial");
                          }
                        }}>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2">
                                <Trash2 className="w-5 h-5 text-destructive" />
                                Before you go...
                              </AlertDialogTitle>
                              <AlertDialogDescription className="space-y-4">
                                <p>
                                  We'd love to hear your feedback! Please consider filling out our optional survey to help us improve.
                                </p>
                                <a
                                  href="https://forms.gle/e7eNRS7nwCULZoSk9"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                                >
                                  Take the feedback survey (optional)
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                </a>
                                <div className="pt-2 border-t">
                                  <p className="text-sm text-muted-foreground">
                                    Click the button below to permanently delete your account.
                                  </p>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => {
                                setShowDeleteDialog(false);
                                setDeleteStep("initial");
                              }}>
                                Cancel
                              </AlertDialogCancel>
                              <Button
                                variant="destructive"
                                disabled={deletingAccount}
                                onClick={async () => {
                                  setDeletingAccount(true);
                                  try {
                                    const { data, error } = await supabase.functions.invoke("delete-account");
                                    if (error) throw error;
                                    if (data.error) throw new Error(data.message || data.error);
                                    
                                    toast({
                                      title: "Account Deleted",
                                      description: "Your account has been permanently deleted.",
                                    });
                                    
                                    // Sign out and redirect
                                    await supabase.auth.signOut();
                                    navigate("/");
                                  } catch (error: any) {
                                    toast({
                                      title: "Error deleting account",
                                      description: error.message || "Please try again or contact support.",
                                      variant: "destructive",
                                    });
                                  } finally {
                                    setDeletingAccount(false);
                                    setShowDeleteDialog(false);
                                    setDeleteStep("initial");
                                  }
                                }}
                              >
                                {deletingAccount ? "Deleting..." : "Delete My Account Forever"}
                              </Button>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <Button type="submit" className="w-full" disabled={saving}>
              {saving 
                ? (isSetupMode ? "Creating Account..." : "Saving...") 
                : (isSetupMode ? "Create Account" : "Save Changes")
              }
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
